import { FormError } from '@/components/ui/FormError';
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { DatePicker } from '@/components/ui/DatePicker';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { priceSourceLabel, priceSourceTooltip } from '@/components/ui/PriceSourceBadge';
import type { LinePriceSource } from '@/shared/pricing/types';
import { isClientAction } from './host';
import type {
  ChoiceCandidate, ChoiceT, ClientActionProposal, CopilotHost, CopilotMsg, CopilotRequest, EmailDraft, ProLine, ProposalT,
} from './host';
import { CartLinesCard, EmailDraftCard } from './cards';

type Msg = CopilotMsg;
// Cards priced by the sales price authority (GET /ai/item-price). Every other
// editable card (the AP bill) keeps the item's list price on pick: vendor
// pricing is not this authority.
const SALES_PRICED_ACTIONS = new Set(['ar_invoice_draft', 'sales_order_draft', 'sales_quote_draft']);

type ChatAnswer = {
  reply?: string; proposal?: ProposalT | ClientActionProposal | null; choice?: ChoiceT | null;
  links?: { label: string; path: string }[]; draft?: EmailDraft | null;
};

const SUGGESTIONS = [
  'What is my current cash position?',
  'How much AR is overdue?',
  'Where is the Report Production form?',
  'Who are my top vendors this year?',
];

const DEFAULT_EMPTY = 'Ask about your finances or where to find things — I read live data (cash, AR/AP, cash flow, vendors) and never change anything.';
const DEFAULT_PLACEHOLDER = 'Ask about cash, AR/AP, where to find things…';

// The words a failed request shows: the server's own message (a 429 from the
// per-user limits, a 503 when AI is not configured), or a plain line for a
// connection that dropped (fetch's TypeError).
function failureText(e: unknown): string {
  if (e instanceof TypeError) return 'Could not reach the server. Check the connection and try again.';
  return e instanceof Error && e.message ? e.message : 'Request failed';
}

const isThenable = <T,>(v: unknown): v is Promise<T> => !!v && typeof (v as { then?: unknown }).then === 'function';

// Voice input uses the browser's built-in SpeechRecognition (Web Speech API)
// so words appear LIVE as the user speaks. Chromium-only (Chrome/Edge); the
// mic button is hidden where it isn't available. Needs a secure context.
const SpeechRecognitionImpl = typeof window !== 'undefined'
  ? ((window as unknown as { SpeechRecognition?: unknown; webkitSpeechRecognition?: unknown }).SpeechRecognition
    || (window as unknown as { webkitSpeechRecognition?: unknown }).webkitSpeechRecognition)
  : undefined;
const micSupported = !!SpeechRecognitionImpl;
const SPEECH_LANG = 'en-US';

/**
 * Reusable AI Companion chat surface. Fills its container height (use inside
 * a sized flex parent). The `host` (./host.ts) is the shell: the ERP's
 * (erpHost.tsx) or the Sales app's. `seed` sends a prompt once per key, after
 * the history has loaded and while online (the Sales app's "Brief me",
 * "Suggest items" and "Draft follow-up" buttons).
 */
export function CopilotChat({ host, seed }: { host: CopilotHost; seed?: { prompt: string; key: string } }) {
  const [boot] = useState(() => {
    const v = host.history.load();
    return isThenable<Msg[] | null>(v) ? { msgs: [] as Msg[], pending: v } : { msgs: v || [], pending: null };
  });
  const [messages, setMessages] = useState<Msg[]>(boot.msgs);
  const [loaded, setLoaded] = useState(!boot.pending);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [listening, setListening] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const offline = host.online === false;
  const showLinks = host.showLinks !== false;
  const suggestions = host.copy?.suggestions ?? SUGGESTIONS;

  useEffect(() => {
    if (!boot.pending) return undefined;
    let live = true;
    boot.pending.then(
      (v) => { if (live) { setMessages(Array.isArray(v) ? v : []); setLoaded(true); } },
      () => { if (live) setLoaded(true); },
    );
    return () => { live = false; };
  }, [boot]);

  function patchMsg(idx: number, patch: Partial<Msg>) {
    setMessages((m) => m.map((x, i) => (i === idx ? { ...x, ...patch } : x)));
  }
  async function approve(idx: number, action: string, payload: unknown) {
    patchMsg(idx, { approving: true, actionErr: undefined });
    try {
      const data = await host.request.post<{ label: string; link: string; kind?: string }>('/ai/actions/execute', { action, payload });
      patchMsg(idx, { approving: false, proposal: undefined, result: { label: data.label, link: data.link, kind: data.kind === 'customer' ? 'customer' : 'draft' } });
      host.onExecuted?.();
    } catch (e) {
      patchMsg(idx, { approving: false, actionErr: failureText(e) });
    }
  }
  // A client action (the Sales app's cart lines, D19) is applied by the host;
  // nothing is created on the server.
  async function applyClient(idx: number, p: ClientActionProposal) {
    if (!host.applyClientAction) {
      patchMsg(idx, { actionErr: 'Adding to a cart works only in the Sales app.' });
      return;
    }
    patchMsg(idx, { approving: true, actionErr: undefined });
    try {
      const done = await host.applyClientAction(p);
      patchMsg(idx, { approving: false, proposal: undefined, result: { label: done.label, link: done.path, kind: 'cart' } });
    } catch (e) {
      patchMsg(idx, { approving: false, actionErr: failureText(e) });
    }
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const recognitionRef = useRef<any>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages, busy]);

  // Persist history through the host whenever it changes, once it has loaded
  // (an empty list before then would wipe the stored conversation).
  useEffect(() => {
    if (!loaded) return;
    if (messages.length) host.history.save(messages);
    else host.history.clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the host's history is fixed for the chat's life
  }, [messages, loaded]);

  // Stop recognition on unmount.
  useEffect(() => () => {
    try { recognitionRef.current?.abort(); } catch { /* noop */ }
  }, []);

  function clearChat() {
    setMessages([]);
    setErr(null);
    host.history.clear();
  }

  // Push-to-talk with LIVE transcription (Web Speech API): hold the mic button
  // (or ⌘/Ctrl+Shift+M) and words appear in the box as you speak; release to
  // send. `holdingRef` tracks the press; `finalRef` accumulates finalised
  // words; `baseRef` preserves any text typed before recording.
  const holdingRef = useRef(false);
  const finalRef = useRef('');
  const baseRef = useRef('');
  const sendOnEndRef = useRef(false);
  // Latest input + send(), so async recognition callbacks read current values.
  const inputRef = useRef(input); inputRef.current = input;
  const sendRef = useRef<((t?: string) => void) | null>(null);

  function liveText() {
    const base = baseRef.current.trim();
    const spoken = finalRef.current.trim();
    return base ? (spoken ? `${base} ${spoken}` : base) : spoken;
  }

  function startRecording() {
    if (listening) return;
    if (!SpeechRecognitionImpl) { setErr('Live transcription needs Chrome or Edge.'); holdingRef.current = false; return; }
    setErr(null);
    baseRef.current = inputRef.current;
    finalRef.current = '';
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const rec: any = new (SpeechRecognitionImpl as any)();
    rec.lang = SPEECH_LANG;
    rec.interimResults = true;
    rec.continuous = true;
    rec.onresult = (ev: { resultIndex: number; results: ArrayLike<{ 0: { transcript: string }; isFinal: boolean }> }) => {
      let interim = '';
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const r = ev.results[i];
        if (r.isFinal) finalRef.current += r[0].transcript;
        else interim += r[0].transcript;
      }
      const base = baseRef.current.trim();
      const spoken = (finalRef.current + interim).trim();
      setInput(base ? (spoken ? `${base} ${spoken}` : base) : spoken);
    };
    rec.onerror = (ev: { error?: string }) => {
      if (ev.error === 'not-allowed' || ev.error === 'service-not-allowed') setErr('Microphone permission was blocked — allow it in your browser.');
      else if (ev.error && !['aborted', 'no-speech'].includes(ev.error)) setErr('Speech recognition error — try again.');
    };
    rec.onend = () => {
      // Chrome can end on a pause; if the user is still holding, keep going.
      if (holdingRef.current) { try { rec.start(); return; } catch { /* fall through */ } }
      setListening(false);
      if (sendOnEndRef.current) {
        sendOnEndRef.current = false;
        const text = liveText().trim();
        if (text) { setInput(''); void sendRef.current?.(text); }
      }
    };
    recognitionRef.current = rec;
    try { rec.start(); setListening(true); } catch { /* already started */ }
  }

  // Stop → onend fires → posts the spoken question immediately.
  function stopRecording() {
    sendOnEndRef.current = true;
    setListening(false);
    try { recognitionRef.current?.stop(); } catch { /* noop */ }
  }

  // Keep the latest start/stop in refs so the global hotkey listener (mounted
  // once) always calls the current closures.
  const startRef = useRef(startRecording); startRef.current = startRecording;
  const stopRef = useRef(stopRecording); stopRef.current = stopRecording;

  // Hold-to-talk hotkey: ⌘/Ctrl+Shift+M while the assistant is open.
  useEffect(() => {
    if (!micSupported) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && (e.key === 'm' || e.key === 'M')) {
        if (holdingRef.current) return; // ignore key auto-repeat
        e.preventDefault();
        holdingRef.current = true;
        void startRef.current();
      }
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (!holdingRef.current) return;
      if (['m', 'M', 'Meta', 'Control', 'Shift'].includes(e.key)) {
        holdingRef.current = false;
        stopRef.current();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => { window.removeEventListener('keydown', onKeyDown); window.removeEventListener('keyup', onKeyUp); };
  }, []);

  // Pointer (mouse/touch) push-to-talk handlers for the mic button.
  const micDown = (e: React.PointerEvent) => { e.preventDefault(); holdingRef.current = true; void startRecording(); };
  const micUp = () => { if (holdingRef.current) { holdingRef.current = false; stopRecording(); } };

  async function send(text?: string) {
    const content = (text ?? input).trim();
    if (!content || busy || !loaded || offline) return;
    if (listening) stopRecording();
    setErr(null);
    const next: Msg[] = [...messages, { role: 'user', content }];
    setMessages(next);
    setInput('');
    setBusy(true);
    try {
      const ctx = host.context || {};
      const data = await host.request.post<ChatAnswer>('/ai/chat', {
        messages: next, context: ctx.label, record: ctx.record ?? null, recordPath: ctx.recordPath, ...(ctx.extra || {}),
      });
      setMessages((m) => [...m, {
        role: 'assistant', content: data.reply || '', proposal: data.proposal || undefined, choice: data.choice || undefined,
        links: data.links?.length ? data.links : undefined, draft: data.draft || undefined,
      }]);
    } catch (e) {
      setErr(failureText(e));
    } finally {
      setBusy(false);
    }
  }
  sendRef.current = send;

  // A seed prompt goes out once per key, after the history has loaded, while
  // online and idle; an offline seed waits for the connection.
  const seedKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!seed || !loaded || offline || busy || seedKeyRef.current === seed.key) return;
    seedKeyRef.current = seed.key;
    void send(seed.prompt);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- send reads the current render's state
  }, [seed?.key, seed?.prompt, loaded, offline, busy]);

  return (
    <div className="flex flex-col h-full min-h-0">
      {messages.length > 0 && (
        <div className="flex justify-end pb-2 -mt-1">
          <button onClick={clearChat} className="text-[12px] text-secondary hover:text-rose-600 transition font-medium">Clear</button>
        </div>
      )}
      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto overscroll-contain space-y-3 pr-1">
        {offline && host.offlineNotice}
        {messages.length === 0 && !offline && loaded && (
          <div className="text-[13px] text-muted">
            <p className="mb-3">{host.copy?.empty ?? DEFAULT_EMPTY}</p>
            <div className="flex flex-wrap gap-2">
              {suggestions.map((s) => (
                <button key={s} onClick={() => send(s)}
                  className="text-[12px] px-2.5 py-1 rounded-full border border-border hover:border-primary hover:text-primary transition text-left">{s}</button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className="space-y-2">
            {m.content && (
              <div className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-[13px] leading-relaxed ${
                  m.role === 'user' ? 'bg-primary text-white' : 'bg-slate-100 text-primary-text'
                }`}><MessageContent text={m.content} inverted={m.role === 'user'} /></div>
              </div>
            )}
            {m.proposal && isClientAction(m.proposal) && (
              <CartLinesCard p={m.proposal} busy={!!m.approving} err={m.actionErr}
                onApprove={() => applyClient(i, m.proposal as ClientActionProposal)}
                onDismiss={() => patchMsg(i, { proposal: undefined })} />
            )}
            {m.proposal && !isClientAction(m.proposal) && (
              <ProposalCard p={m.proposal} request={host.request} busy={!!m.approving} err={m.actionErr}
                onApprove={(payload) => approve(i, (m.proposal as ProposalT).action, payload)}
                onOpenForm={(link) => { patchMsg(i, { proposal: undefined }); host.navigate(link); }}
                onDismiss={() => patchMsg(i, { proposal: undefined })} />
            )}
            {m.choice && (
              <ChoiceCard c={m.choice} request={host.request} resolved={m.choiceResolved}
                onPick={(label, msg) => { patchMsg(i, { choiceResolved: label }); send(msg); }} />
            )}
            {m.draft && <EmailDraftCard draft={m.draft} />}
            {showLinks && m.links && m.links.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {m.links.map((l, k) => (
                  <button key={k} onClick={() => host.navigate(l.path)}
                    className="inline-flex items-center gap-1 text-[12px] font-medium text-primary border border-primary/30 bg-primary/5 rounded-lg px-2.5 py-1 hover:bg-primary/10 transition">
                    {l.label} →
                  </button>
                ))}
              </div>
            )}
            {m.result && m.result.kind === 'cart' && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-[12.5px] text-emerald-800 flex items-center justify-between gap-2">
                <span>{m.result.label}</span>
                {m.result.link && <button onClick={() => host.navigate(m.result!.link!)} className="font-semibold text-primary hover:underline shrink-0">Open cart</button>}
              </div>
            )}
            {m.result && m.result.kind !== 'cart' && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-[12.5px] text-emerald-800 flex items-center justify-between gap-2">
                <span>✓ {m.result.label} created{m.result.kind === 'customer' ? '' : ' (draft)'}</span>
                {m.result.link && <button onClick={() => host.navigate(m.result!.link!)} className="font-semibold text-primary hover:underline shrink-0">Open →</button>}
              </div>
            )}
          </div>
        ))}
        {busy && <div className="text-[12px] text-muted">Thinking…</div>}
      </div>
      <FormError className="text-[12px] text-red-600 mt-2">{err}</FormError>
      <div className="flex items-center gap-2 mt-3 border-t border-border-light pt-3 shrink-0">
        {micSupported && !offline && (
          <button
            onPointerDown={micDown} onPointerUp={micUp} onPointerLeave={micUp} onPointerCancel={micUp}
            onContextMenu={(e) => e.preventDefault()}
            title={listening ? 'Release to send' : 'Hold to talk (⌘/Ctrl+Shift+M)'}
            className={`shrink-0 h-9 w-9 rounded-lg border flex items-center justify-center transition select-none touch-none ${
              listening ? 'border-rose-400 text-rose-600 bg-rose-50 animate-pulse'
                : 'border-border text-secondary hover:text-primary hover:border-primary'
            }`}>
            <MicIcon />
          </button>
        )}
        <Input value={input} onChange={(e) => setInput(e.target.value)}
          placeholder={listening ? 'Listening… release to send' : (host.copy?.placeholder ?? DEFAULT_PLACEHOLDER)}
          onPaste={(e) => {
            // Pasting tab-separated content (e.g. copied from Excel/Sheets):
            // convert it to a markdown table so it renders as a table.
            const text = e.clipboardData.getData('text');
            if (text.includes('\t') && text.includes('\n')) {
              e.preventDefault();
              const md = tsvToMarkdown(text);
              setInput((prev) => (prev ? `${prev}\n${md}` : md));
            }
          }}
          onKeyDown={(e) => { if (e.key === 'Enter') send(); }} disabled={busy || offline || !loaded} />
        <Button onClick={() => send()} loading={busy} disabled={!input.trim() || offline || !loaded}>Send</Button>
      </div>
    </div>
  );
}

const fieldCls = 'h-8 rounded-md border border-border bg-surface px-2 text-[12.5px] text-primary-text';

// "Pick an item" card: selectable candidate rows (with price + currency) plus
// an "Other — search a product" option. Picking sends a follow-up message so
// the copilot proposes the draft with the chosen item.
function ChoiceCard({ c, request, resolved, onPick }: {
  c: ChoiceT; request: CopilotRequest; resolved?: string; onPick: (label: string, followUp: string) => void;
}) {
  const noun = c.kind === 'customer' ? 'customer' : c.kind === 'vendor' ? 'vendor' : 'item';
  const searchPath = c.kind === 'customer' ? '/ai/customer-search'
    : c.kind === 'vendor' ? '/ai/vendor-search'
    : '/ai/item-search';
  const [otherOpen, setOtherOpen] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState<ChoiceCandidate[]>([]);
  const debounced = useDebounced(q, 250);
  useEffect(() => {
    let live = true;
    if (!otherOpen || !debounced.trim()) { setResults([]); return; }
    request.get<ChoiceCandidate[]>(searchPath, { q: debounced })
      .then((rows) => { if (live) setResults(rows || []); }).catch(() => {});
    return () => { live = false; };
  }, [debounced, otherOpen, searchPath, request]);

  const pick = (code: string, name: string) => onPick(`${name} · ${code}`,
    c.kind === 'customer' ? `Use the customer ${name} (${code}).`
      : c.kind === 'vendor' ? `Use the vendor ${name} (${code}).`
      : `Use the item ${code} (${name}) for that.`);
  const Row = ({ it }: { it: ChoiceCandidate }) => (
    <button key={it.id} onClick={() => pick(it.code, it.name)}
      className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg border border-border bg-surface hover:border-primary hover:bg-primary/5 transition text-left">
      <span className="w-3.5 h-3.5 rounded-full border border-border shrink-0" />
      <span className="flex-1 min-w-0">
        <span className="text-primary-text">{it.name}</span> <span className="text-muted">· {it.code}</span>
        {it.note && <span className="text-muted"> · {it.note}</span>}
      </span>
      {it.price != null && <span className="text-secondary font-numeric tabular-nums shrink-0">{it.currency} {Number(it.price).toFixed(2)}</span>}
    </button>
  );

  if (resolved) {
    return <div className="rounded-xl border border-border bg-canvas-soft px-3 py-2 text-[12.5px] text-secondary">Chose <span className="font-medium text-primary-text">{resolved}</span></div>;
  }
  return (
    <div className="rounded-xl border border-primary/30 bg-primary/5 p-2.5 text-[12.5px] space-y-1.5">
      <div className="text-[11px] text-secondary px-0.5">Pick a {noun}{c.note ? ` ${c.note}` : ''}:</div>
      {c.candidates.map((it) => <Row key={it.id} it={it} />)}
      {!otherOpen ? (
        <button onClick={() => setOtherOpen(true)}
          className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg border border-dashed border-border text-secondary hover:text-primary hover:border-primary transition text-left">
          <span className="w-3.5 h-3.5 rounded-full border border-border shrink-0" />
          <span>Other — search a {noun}…</span>
        </button>
      ) : (
        <div className="relative">
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${noun} or code…`} className={`${fieldCls} w-full`} />
          {results.length > 0 && (
            <div className="absolute z-10 mt-1 left-0 right-0 bg-surface border border-border rounded-lg shadow-lg max-h-44 overflow-y-auto">
              {results.map((it) => (
                <button key={it.id} type="button" onClick={() => pick(it.code, it.name)}
                  className="block w-full text-left px-2.5 py-1.5 text-[12.5px] hover:bg-slate-50">
                  <span className="text-primary-text">{it.name}</span> <span className="text-muted">· {it.code}{it.note ? ` · ${it.note}` : ''}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Strip leading zeros (e.g. "007" -> 7, "0.5" stays 0.5) for price/qty fields.
function noLeadingZero(v: string): number { return Number(v); }

// A cleared price field is no price (null, so the server prices the line or
// asks for one), never 0.
function priceFromInput(v: string): number | null { return v.trim() === '' ? null : Number(v); }

// Clicking into a zero field empties it (type fresh, no stuck "0"); non-zero
// selects all so the next keystroke overwrites.
function clearZeroFocus(e: { currentTarget: HTMLInputElement }) {
  if (Number(e.currentTarget.value) === 0) e.currentTarget.value = '';
  else e.currentTarget.select();
}

function useDebounced<T>(value: T, ms = 250): T {
  const [v, setV] = useState(value);
  useEffect(() => { const id = setTimeout(() => setV(value), ms); return () => clearTimeout(id); }, [value, ms]);
  return v;
}

// Convert pasted tab-separated values (Excel/Sheets) into a markdown table
// that the renderer below turns into a real table. First row = header.
function tsvToMarkdown(tsv: string): string {
  const lines = tsv.replace(/\r/g, '').split('\n').filter((l) => l.trim() !== '');
  if (lines.length < 2) return tsv;
  const rows = lines.map((l) => l.split('\t').map((c) => c.trim().replace(/\|/g, '\\|')));
  const cols = Math.max(...rows.map((r) => r.length));
  const pad = (r: string[]) => { const a = [...r]; while (a.length < cols) a.push(''); return a; };
  const fmt = (r: string[]) => `| ${pad(r).join(' | ')} |`;
  return [fmt(rows[0]), `| ${Array(cols).fill('---').join(' | ')} |`, ...rows.slice(1).map(fmt)].join('\n');
}

// ---- Tiny markdown renderer (tables, lists, **bold**, `code`) -------------
type MdBlock =
  | { type: 'table'; header: string[]; rows: string[][] }
  | { type: 'list'; ordered: boolean; items: string[] }
  | { type: 'para'; text: string };

function splitCells(line: string): string[] {
  let s = line.trim();
  if (s.startsWith('|')) s = s.slice(1);
  if (s.endsWith('|')) s = s.slice(0, -1);
  return s.split('|').map((c) => c.trim());
}
const isSep = (line: string) => /^[\s|:-]+$/.test(line.trim()) && line.includes('-');

function parseBlocks(text: string): MdBlock[] {
  const lines = text.replace(/\r/g, '').split('\n');
  const blocks: MdBlock[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (line.includes('|') && i + 1 < lines.length && isSep(lines[i + 1])) {
      const header = splitCells(line);
      const rows: string[][] = [];
      i += 2;
      while (i < lines.length && lines[i].includes('|') && lines[i].trim() !== '') { rows.push(splitCells(lines[i])); i++; }
      blocks.push({ type: 'table', header, rows });
      continue;
    }
    if (/^\s*([-*]|\d+\.)\s+/.test(line)) {
      const ordered = /^\s*\d+\.\s/.test(line);
      const items: string[] = [];
      while (i < lines.length && /^\s*([-*]|\d+\.)\s+/.test(lines[i])) { items.push(lines[i].replace(/^\s*([-*]|\d+\.)\s+/, '')); i++; }
      blocks.push({ type: 'list', ordered, items });
      continue;
    }
    if (line.trim() === '') { i++; continue; }
    const para: string[] = [];
    while (i < lines.length && lines[i].trim() !== ''
      && !(lines[i].includes('|') && i + 1 < lines.length && isSep(lines[i + 1]))
      && !/^\s*([-*]|\d+\.)\s+/.test(lines[i])) { para.push(lines[i]); i++; }
    blocks.push({ type: 'para', text: para.join('\n') });
  }
  return blocks;
}

function renderInline(text: string): ReactNode[] {
  const out: ReactNode[] = [];
  const re = /(\*\*[^*]+\*\*|`[^`]+`)/g;
  let last = 0; let m: RegExpExecArray | null;
  while ((m = re.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith('**')) out.push(<strong key={out.length}>{tok.slice(2, -2)}</strong>);
    else out.push(<code key={out.length} className="px-1 rounded bg-black/10 font-numeric text-[11.5px]">{tok.slice(1, -1)}</code>);
    last = re.lastIndex;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

function MessageContent({ text, inverted }: { text: string; inverted: boolean }) {
  const blocks = useMemo(() => parseBlocks(text), [text]);
  const b = inverted ? 'border-white/25' : 'border-border';
  return (
    <div className="space-y-2">
      {blocks.map((blk, i) => {
        if (blk.type === 'table') {
          return (
            <div key={i} className="overflow-x-auto -mx-0.5">
              <table className="text-[12px] border-collapse">
                <thead><tr>{blk.header.map((h, k) => <th key={k} className={`border ${b} px-2 py-1 text-left font-semibold`}>{renderInline(h)}</th>)}</tr></thead>
                <tbody>{blk.rows.map((r, ri) => <tr key={ri}>{r.map((cell, ci) => <td key={ci} className={`border ${b} px-2 py-1 align-top`}>{renderInline(cell)}</td>)}</tr>)}</tbody>
              </table>
            </div>
          );
        }
        if (blk.type === 'list') {
          return blk.ordered
            ? <ol key={i} className="list-decimal pl-5 space-y-0.5">{blk.items.map((it, j) => <li key={j}>{renderInline(it)}</li>)}</ol>
            : <ul key={i} className="list-disc pl-5 space-y-0.5">{blk.items.map((it, j) => <li key={j}>{renderInline(it)}</li>)}</ul>;
        }
        return <p key={i} className="whitespace-pre-wrap leading-relaxed">{renderInline(blk.text)}</p>;
      })}
    </div>
  );
}

// is_taxable false: a product exempt from sales tax (its sales line carries 0%).
type ItemHit = { id: string; code: string; name: string; list_price: number | null; is_taxable?: boolean };

// The sales price authority's answer for one card line (GET /ai/item-price at
// the line qty, default 1, in the document currency when the card has one).
// null when nothing prices the item or the lookup is refused or fails: the
// card then shows no price and the server prices or refuses at execute.
async function authorityPrice(request: CopilotRequest, itemId: string, { customerId, date, qty, currency }: {
  customerId?: string; date?: string; qty?: number | null; currency?: string;
}): Promise<{ price: number; source: LinePriceSource | null } | null> {
  const n = Number(qty);
  try {
    // Empty values are left out; the server reads them as absent.
    const data = await request.get<{ price?: unknown; source?: LinePriceSource | null }>('/ai/item-price', {
      customer: customerId || undefined, item: itemId, date: date || undefined,
      qty: String(Number.isFinite(n) && n > 0 ? n : 1), currency: currency || undefined,
    });
    const price = data?.price;
    if (price == null || price === '' || !Number.isFinite(Number(price))) return null;
    return { price: Number(price), source: data.source ?? null };
  } catch {
    return null;
  }
}

// Searchable description: type to find an item (on a sales card the price
// fills from the price authority on pick; on the AP bill from the item's list
// price) or just type a free-text description. A pick tells the card whether
// the item is taxable (its sales tax, see ProposalCard).
function ItemPicker({ request, description, customerId, date, qty, currency, pricing, label, onChange, onType }: {
  request: CopilotRequest; description: string; customerId?: string; date?: string; qty?: number | null; currency?: string;
  pricing: 'sales' | 'item_list'; label?: string; onChange: (patch: Partial<ProLine>, taxable: boolean) => void;
  // Text typed in the field (no item picked): the card drops the line's item.
  onType: (text: string) => void;
}) {
  const [q, setQ] = useState(description);
  // The latest pick (or typing) on this line. A price answer for an earlier
  // pick that lands after it is dropped, so a slow answer never replaces the
  // item the user picked last.
  const pickSeq = useRef(0);
  const [results, setResults] = useState<ItemHit[]>([]);
  const [open, setOpen] = useState(false);
  const debounced = useDebounced(q, 250);
  useEffect(() => {
    let live = true;
    // Fetch whenever the dropdown is open — including with an empty query, so
    // clicking the field shows a default list of items (the endpoint returns
    // the top items for an empty q) instead of nothing.
    if (!open) { setResults([]); return; }
    request.get<ItemHit[]>('/ai/item-search', { q: debounced || undefined })
      .then((rows) => { if (live) setResults(rows || []); }).catch(() => {});
    return () => { live = false; };
  }, [debounced, open, request]);
  async function pick(it: ItemHit) {
    const seq = ++pickSeq.current;
    const taxable = it.is_taxable !== false;
    setQ(it.name); setOpen(false);
    if (pricing === 'item_list') {
      onChange({ description: it.name, item_id: it.id, ...(it.list_price != null ? { unit_price: it.list_price } : {}) }, taxable);
      return;
    }
    const got = await authorityPrice(request, it.id, { customerId, date, qty, currency });
    if (pickSeq.current !== seq) return; // a later pick or typing on this line came first
    onChange(got
      ? { description: it.name, item_id: it.id, unit_price: got.price, price_auto: true, auto_unit_price: got.price, auto_item_id: it.id, price_source: got.source }
      // Never the item list price: an empty price is priced (or refused) by
      // the server at execute, never sent as a typed price.
      : { description: it.name, item_id: it.id, unit_price: null, price_auto: false, auto_unit_price: null, auto_item_id: null, price_source: null }, taxable);
  }
  return (
    <div className="relative">
      <input value={q} placeholder="Search item or type…" aria-label={label}
        onChange={(e) => { pickSeq.current++; setQ(e.target.value); setOpen(true); onType(e.target.value); }}
        onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)}
        className={`${fieldCls} w-full`} />
      {open && results.length > 0 && (
        <div className="absolute z-10 mt-1 left-0 w-60 max-w-[260px] bg-surface border border-border rounded-lg shadow-lg max-h-44 overflow-y-auto">
          {results.map((it) => (
            <button key={it.id} type="button" onMouseDown={(e) => { e.preventDefault(); pick(it); }}
              className="block w-full text-left px-2.5 py-1.5 text-[12.5px] hover:bg-slate-50">
              <span className="text-primary-text">{it.name}</span> <span className="text-muted">· {it.code}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ProposalCard({ p, request, busy, err, onApprove, onOpenForm, onDismiss }: {
  p: ProposalT; request: CopilotRequest; busy: boolean; err?: string; onApprove: (payload: unknown) => void; onOpenForm?: (link: string) => void; onDismiss: () => void;
}) {
  const editable = !!p.editable && Array.isArray(p.payload?.lines);
  // A record (a new customer) is created as it stands, not as a draft to post.
  const record = p.action === 'customer_create';
  // ccy = the document currency CODE (used on the amounts). meta.currency is a
  // descriptive label ("USD @ 1.37 → CAD") shown in the header only.
  const ccy = (p.payload?.currency_code as string) || '';
  const pricing = SALES_PRICED_ACTIONS.has(p.action) ? 'sales' : 'item_list';
  const fx = Number(p.payload?.fx_rate || 1);
  const functional = p.meta?.functional;
  const showFunctional = !!functional && functional !== ccy;
  const [date, setDate] = useState(p.payload?.date || '');
  const [due, setDue] = useState(p.payload?.due_date || '');
  const [memo, setMemo] = useState(p.payload?.memo || '');
  const [lines, setLines] = useState<ProLine[]>(() => (p.payload?.lines || []).map((l) => ({ ...l })));
  const setLine = (i: number, patch: Partial<ProLine>) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  // Edits that a price the authority gave (price_auto) cannot survive, each
  // decided on the line as it stands. A new qty: the price shown was for the
  // old qty (quantity breaks), so it is cleared and the server prices the
  // line at the new qty; a typed price stays. Text typed in the item field:
  // the item is gone, so its price and flags go too (a price kept for an
  // itemless line would be sent as typed).
  const setQty = (i: number, qty: number) => setLines((ls) => ls.map((l, j) => (j !== i ? l
    : { ...l, qty, ...(l.price_auto ? { unit_price: null, price_source: null } : {}) })));
  // Sales tax on a sales card (services/item-tax.js): an item picked on a line
  // shows 0% when it is not taxable and the line's rate (taxable_rate, the
  // model's) when it is, so the card's total stays the draft's; text typed in
  // the item field drops the item, and the line carries its rate as a line
  // with no item does. The AP bill is not sales tax.
  const lineTax = (l: ProLine, taxable: boolean): Partial<ProLine> => (pricing !== 'sales' ? {}
    : !taxable ? { tax_rate: 0 } : l.taxable_rate != null ? { tax_rate: l.taxable_rate } : {});
  const pickItem = (i: number, patch: Partial<ProLine>, taxable: boolean) => setLines((ls) => ls.map((l, j) => (j !== i ? l
    : { ...l, ...patch, ...lineTax(l, taxable) })));
  const typeItem = (i: number, text: string) => setLines((ls) => ls.map((l, j) => (j !== i ? l
    : { ...l, description: text, item_id: null, ...lineTax(l, true),
      ...(l.price_auto ? { unit_price: null, price_auto: false, auto_unit_price: null, auto_item_id: null, price_source: null } : {}) })));
  const removeLine = (i: number) => setLines((ls) => ls.filter((_, j) => j !== i));
  const total = lines.reduce((s, l) => s + (Number(l.qty || 0) * Number(l.unit_price || 0)) * (1 + Number(l.tax_rate || 0) / 100), 0);
  const submit = () => onApprove(editable ? { ...p.payload, date, due_date: due, memo, lines } : p.payload);

  return (
    <div className="rounded-xl border border-primary/30 bg-primary/5 p-3 text-[12.5px]">
      <div className="font-semibold text-primary-text">{p.title || 'Proposed draft'}</div>
      {p.meta?.customer && (
        <div className="text-[12px] text-secondary mb-2">{p.meta.customer}{p.meta.terms ? ` · ${p.meta.terms}` : ''}{p.meta.currency ? ` · ${p.meta.currency}` : ''}</div>
      )}

      {editable ? (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <label className="flex flex-col gap-0.5"><span className="text-[11px] text-secondary">{p.labels?.date || 'Invoice date'}</span>
              <DatePicker value={date} onChange={(v) => setDate(v)} triggerClassName={fieldCls} /></label>
            <label className="flex flex-col gap-0.5"><span className="text-[11px] text-secondary">{p.labels?.due || 'Due date'}</span>
              <DatePicker value={due} onChange={(v) => setDue(v)} triggerClassName={fieldCls} /></label>
          </div>
          <div className="space-y-1.5">
            <div className="grid grid-cols-[1fr_3rem_4.5rem_1rem] gap-1 text-[10.5px] text-secondary uppercase tracking-wide">
              <span>Description</span><span className="text-right">Qty</span><span className="text-right">Price</span><span />
            </div>
            {lines.map((l, i) => (
              <div key={i} className="grid grid-cols-[1fr_3rem_4.5rem_1rem] gap-1 items-center">
                <ItemPicker request={request} description={l.description || ''} customerId={p.payload.customer_id as string | undefined} date={date}
                  qty={l.qty} currency={ccy || undefined} pricing={pricing}
                  label={`Line ${i + 1} item`} onChange={(patch, taxable) => pickItem(i, patch, taxable)} onType={(text) => typeItem(i, text)} />
                <input type="number" inputMode="decimal" aria-label={`Line ${i + 1} qty`} value={l.qty ?? ''} onFocus={clearZeroFocus} onChange={(e) => setQty(i, noLeadingZero(e.target.value))} className={`${fieldCls} text-right px-1`} />
                <input type="number" inputMode="decimal" aria-label={`Line ${i + 1} price`} value={l.unit_price ?? ''} onFocus={clearZeroFocus}
                  // A typed (or cleared) price is a typed price: it no longer
                  // stands for the authority's price, so price_auto goes.
                  onChange={(e) => setLine(i, { unit_price: priceFromInput(e.target.value), price_auto: false, price_source: null })}
                  className={`${fieldCls} text-right px-1`} />
                <button onClick={() => removeLine(i)} disabled={lines.length <= 1} className="text-muted hover:text-rose-600 disabled:opacity-30 text-center leading-none">×</button>
                {l.price_source && (
                  <span className="col-start-3 -mt-0.5 truncate text-right text-[10.5px] leading-tight text-muted"
                    title={priceSourceTooltip({ source: l.price_source, currency: ccy || undefined })}>
                    {priceSourceLabel({ source: l.price_source })}
                  </span>
                )}
              </div>
            ))}
            <button onClick={() => setLines((ls) => [...ls, { description: '', qty: 1, unit_price: null, tax_rate: 0 }])}
              className="text-[12px] text-primary font-medium hover:underline">＋ Add line</button>
          </div>
          <input value={memo} onChange={(e) => setMemo(e.target.value)} className={`${fieldCls} w-full`} placeholder="Memo (optional)" />
          <div className="flex justify-between items-center pt-0.5 text-[12.5px]">
            <span className="text-secondary">Total</span>
            <span className="font-semibold text-primary-text tabular-nums">{total.toFixed(2)} {ccy}</span>
          </div>
          {showFunctional && (
            <div className="flex justify-between items-center text-[11.5px] text-muted">
              <span>In {functional} (system)</span>
              <span className="tabular-nums">{(total * fx).toFixed(2)} {functional}</span>
            </div>
          )}
        </div>
      ) : (
        <dl className="space-y-1 mb-1">
          {Object.entries(p.display || {}).map(([k, v]) => (
            <div key={k} className="flex gap-2"><dt className="text-secondary w-20 shrink-0">{k}</dt>
              <dd className="text-primary-text flex-1 min-w-0">{Array.isArray(v) ? <ul className="space-y-0.5">{v.map((x, i) => <li key={i}>{x}</li>)}</ul> : v}</dd></div>
          ))}
        </dl>
      )}

      <FormError className="text-rose-600 my-2">{err}</FormError>
      {p.formLink ? (
        <>
          <div className="flex gap-2 mt-2.5">
            <Button size="sm" onClick={() => onOpenForm?.(p.formLink!)}>Open the form to report →</Button>
            <Button size="sm" variant="ghost" onClick={onDismiss}>Dismiss</Button>
          </div>
          <div className="text-[11px] text-muted mt-1.5">Opens the Report Production form for this order — review the quantity, warehouse and components there, then post it.</div>
        </>
      ) : (
        <>
          <div className="flex gap-2 mt-2.5">
            <Button size="sm" onClick={submit} loading={busy}>{record ? 'Approve & create customer' : 'Approve & create draft'}</Button>
            <Button size="sm" variant="ghost" onClick={onDismiss} disabled={busy}>Dismiss</Button>
          </div>
          <div className="text-[11px] text-muted mt-1.5">{record ? 'Creates the customer record — you can edit it afterwards.' : 'Creates an unposted draft — you review & post it. Nothing hits the ledger.'}</div>
        </>
      )}
    </div>
  );
}

function MicIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="2" width="6" height="12" rx="3" />
      <path d="M5 10a7 7 0 0 0 14 0" />
      <line x1="12" y1="19" x2="12" y2="22" />
    </svg>
  );
}
